import { useTranslation } from "react-i18next";

import { Form } from "@av/components/atoms/form/Form";
import { Input } from "@av/components/atoms/input/Input";
import { Modal } from "@av/components/atoms/Modal";
import { Typography } from "@av/components/atoms/typography/Typography";
import { Switch } from "@av/components/molecules/Switch";
import {
  getCompanyDocumentValidationError,
  getFileListFromEvent,
  getNativeFile,
  setNameValidationError,
  validatePdfFile,
} from "../company-document-form";
import { MODAL_WIDTH } from "../constants";
import { CompanyKnowledgeUpload } from "./CompanyKnowledgeUpload";
import { useFileError } from "../hooks/useFileError";
import { useCompanyKnowledgeStyles } from "../styles";
import { ModalActions } from "./ModalActions";

import type { CompanyDocumentMutationValues, DocumentFormValues } from "../types";

type AddDocumentModalProps = {
  isSaving?: boolean;
  open: boolean;
  onAdd: (values: CompanyDocumentMutationValues) => Promise<void>;
  onCancel: () => void;
};

export function AddDocumentModal({ isSaving, open, onAdd, onCancel }: AddDocumentModalProps) {
  const { t } = useTranslation();
  const { styles } = useCompanyKnowledgeStyles();
  const [form] = Form.useForm<DocumentFormValues>();
  const { fileError, setFileError, clearFileError } = useFileError();
  const fileErrorId = "company-knowledge-add-file-error";

  const handleCancel = () => {
    onCancel();
    form.resetFields();
    clearFileError();
  };

  return (
    <Modal
      destroyOnHidden
      footer={null}
      okText={t("platform.adminSettings.companyKnowledge_add")}
      onCancel={handleCancel}
      open={open}
      testIdPrefix="company-knowledge-add-document"
      title={t("platform.adminSettings.companyKnowledge_addDocument")}
      width={MODAL_WIDTH}
    >
      <Form
        form={form}
        initialValues={{ file: [], useInAva: true }}
        onFieldsChange={(changedFields) => {
          const fileField = changedFields.find((field) => field.name[0] === "file");

          if (fileField) {
            setFileError(fileField.errors?.[0]);
          }
        }}
        onFinish={async (values) => {
          const file = getNativeFile(values.file?.[0]);

          if (!file) {
            return;
          }

          try {
            await onAdd({
              name: values.name,
              description: values.description,
              useInAva: values.useInAva,
              file,
            });
            form.resetFields();
            clearFileError();
          } catch (error) {
            const filenameValidationError = getCompanyDocumentValidationError(error, "filename");
            const nameValidationError = getCompanyDocumentValidationError(error, "name");

            setFileError(
              filenameValidationError ??
                (nameValidationError
                  ? undefined
                  : t("platform.adminSettings.companyKnowledge_uploadFailed"))
            );
            setNameValidationError(form, error);
            // Keep the modal open so the user can retry.
          }
        }}
      >
        <div className={styles.uploadFormBlock}>
          <Form.Item
            name="file"
            noStyle
            getValueFromEvent={getFileListFromEvent}
            rules={[{ validator: (_rule, value) => validatePdfFile(t, value) }]}
            testIdPrefix="company-knowledge-add-document-file"
            validateTrigger={["onChange", "onSubmit"]}
            valuePropName="fileList"
          >
            <CompanyKnowledgeUpload
              errorId={fileErrorId}
              fileInputTestId="company-knowledge-add-document-file-input"
              fileError={fileError}
              testIdPrefix="company-knowledge-add-document-upload"
            />
          </Form.Item>
          {fileError && (
            <Typography.Text
              id={fileErrorId}
              role="alert"
              testIdPrefix="company-knowledge-add-document-file-error"
              type="danger"
            >
              {fileError}
            </Typography.Text>
          )}
        </div>
        <Form.Item
          label={t("platform.adminSettings.companyKnowledge_name")}
          name="name"
          rules={[
            {
              required: true,
              message: t("platform.adminSettings.companyKnowledge_nameRequired"),
            },
            {
              whitespace: true,
              message: t("platform.adminSettings.companyKnowledge_nameRequired"),
            },
          ]}
          testIdPrefix="company-knowledge-add-document-name"
        >
          <Input
            placeholder={t("platform.adminSettings.companyKnowledge_namePlaceholder")}
            testIdPrefix="company-knowledge-add-document-name"
          />
        </Form.Item>
        <Form.Item
          label={t("platform.adminSettings.companyKnowledge_description")}
          name="description"
          testIdPrefix="company-knowledge-add-document-description"
        >
          <Input.TextArea
            placeholder={t("platform.adminSettings.companyKnowledge_descriptionPlaceholder")}
            testIdPrefix="company-knowledge-add-document-description"
          />
        </Form.Item>
        <Form.Item
          className={styles.switchFormItem}
          label={t("platform.adminSettings.companyKnowledge_avaAccess")}
          name="useInAva"
          testIdPrefix="company-knowledge-add-document-ava-access"
          valuePropName="checked"
        >
          <Switch testIdPrefix="company-knowledge-add-document-ava-access" />
        </Form.Item>
        <ModalActions
          cancelTestIdPrefix="company-knowledge-add-document-cancel"
          isSaving={isSaving}
          okText={t("platform.adminSettings.companyKnowledge_add")}
          okTestIdPrefix="company-knowledge-add-document-submit"
          onCancel={handleCancel}
        />
      </Form>
    </Modal>
  );
}
